import { useState, useRef, useCallback, type ReactNode } from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  useHover,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  type Placement,
} from "@floating-ui/react";
import { useIsSmallScreen, viewportClamp } from "../lib/floating";
import { MapPin, Star, MessageSquare, ExternalLink, Utensils, RotateCcw, Gauge, AlertTriangle, ShieldAlert } from "lucide-react";
import { useRestaurantHoverDetails, type RestaurantHoverDetails } from "../api/useRestaurantHoverDetails";

export interface PastVisit {
  firstName: string;
  date: string;
}

interface RestaurantHoverCardProps {
  restaurantId?: string;
  children: ReactNode;
  className?: string;
  placement?: Placement;
  pastVisits?: PastVisit[];
  /** Set false when the child has its own tap action (e.g. a select button) so small-screen tap doesn't also open the sheet. */
  tapToOpen?: boolean;
}

const RATING_LABELS: Record<string, { label: string; color: string }> = {
  perfect: { label: "Perfect", color: "text-success" },
  good: { label: "Good", color: "text-info" },
  okay: { label: "Okay", color: "text-warning" },
  notGreat: { label: "Not Great", color: "text-orange-400" },
  poor: { label: "Poor", color: "text-error" },
};

/**
 * Banded against the platform mean (3.60) and the 3.20 line the audit rubric
 * treats as a fail, so the colour means the same thing here as in the rubric.
 */
function scoreColor(score: number): string {
  if (score >= 3.8) return "text-success";
  if (score >= 3.6) return "text-info";
  if (score >= 3.2) return "text-warning";
  return "text-error";
}

export function RestaurantHoverCard({ restaurantId, children, className, placement: preferredPlacement = "right-start", pastVisits, tapToOpen = true }: RestaurantHoverCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const isSmallScreen = useIsSmallScreen();

  const { data, isLoading } = useRestaurantHoverDetails(restaurantId, shouldFetch);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      setIsOpen(open);
      if (open) {
        setShouldFetch(true);
      } else {
        clearTimeout(hoverTimerRef.current);
      }
    },
    middleware: isSmallScreen
      ? [] // the bottom sheet is CSS-positioned; anchored middleware would fight it
      : [offset(6), flip(), ...viewportClamp(320)],
    whileElementsMounted: autoUpdate,
    placement: preferredPlacement,
  });

  const hover = useHover(context, {
    enabled: !isSmallScreen,
    delay: { open: 300, close: 200 },
    move: false,
  });
  const click = useClick(context, { enabled: isSmallScreen && tapToOpen });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: isSmallScreen ? "dialog" : "tooltip" });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    click,
    dismiss,
    role,
  ]);

  const handleMouseEnter = useCallback(() => {
    hoverTimerRef.current = setTimeout(() => {
      setShouldFetch(true);
    }, 150);
  }, []);

  const handleMouseLeave = useCallback(() => {
    clearTimeout(hoverTimerRef.current);
  }, []);

  if (!restaurantId) {
    return <span className={className}>{children}</span>;
  }

  const details = data;
  const stats = details?.stats;
  const totalRatings = stats
    ? Object.values(stats.ratingDistribution).reduce((a: number, b: number) => a + b, 0)
    : 0;

  return (
    <>
      <span
        ref={refs.setReference}
        {...getReferenceProps()}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`cursor-default ${className || ""}`}
      >
        {children}
      </span>

      {isOpen && (
        <FloatingPortal>
          {isSmallScreen && (
            <div
              className="fixed inset-0 z-[9998] bg-black/50"
              onClick={() => setIsOpen(false)}
            />
          )}
          <div
            ref={refs.setFloating}
            style={isSmallScreen ? undefined : floatingStyles}
            {...getFloatingProps()}
            className={
              isSmallScreen
                ? "fixed inset-x-0 bottom-0 z-[9999] bg-dark-surface border-t border-dark-border rounded-t-2xl shadow-2xl max-h-[75vh] overflow-y-auto pb-[env(safe-area-inset-bottom)]"
                : "z-[9999] bg-dark-surface border border-dark-border rounded-xl shadow-2xl w-80 overflow-y-auto"
            }
          >
            {isSmallScreen && (
              <div className="sticky top-0 z-10 flex justify-center pt-2 pb-1 bg-dark-surface">
                <div className="w-9 h-1 rounded-full bg-dark-border" />
              </div>
            )}
            {isLoading || !details ? (
              <div className="p-4 flex items-center justify-center gap-2">
                <span className="loading loading-spinner loading-sm text-primary"></span>
                <span className="text-sm text-dark-muted">Loading...</span>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="p-3 border-b border-dark-border">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h4 className="font-semibold text-sm truncate">{details.restaurant.name}</h4>
                      <div className="flex items-center gap-1 text-xs text-dark-muted mt-0.5">
                        <MapPin className="w-3 h-3 shrink-0" />
                        <span className="truncate">{details.restaurant.address}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {details.restaurant.googleMapsLink && (
                        <a
                          href={details.restaurant.googleMapsLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-ghost btn-xs p-1"
                          title="Google Maps"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Tags */}
                  {details.restaurant.tags && details.restaurant.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {details.restaurant.tags.map((tag: string) => (
                        <span
                          key={tag}
                          className="px-1.5 py-0.5 bg-accent/10 text-accent text-[10px] rounded"
                        >
                          {tag.replace(/_/g, " ")}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Venue Score — the number the matcher ranks on */}
                {details.venueScore && (
                  <div className="p-3 border-b border-dark-border">
                    <div className="flex items-baseline gap-2">
                      <Gauge className="w-3 h-3 text-dark-muted self-center shrink-0" />
                      <span className="text-[10px] font-semibold text-dark-muted uppercase tracking-wide">
                        Venue Score
                      </span>
                      <span className={`ml-auto font-bold text-base ${scoreColor(details.venueScore.score)}`}>
                        {details.venueScore.score.toFixed(2)}
                      </span>
                      <span className="text-[10px] text-dark-muted">/ 5</span>
                    </div>

                    {/* Where the score came from. A venue nobody has been sent to
                        scores entirely on research, and that must be visible. */}
                    <div className="mt-1.5 flex h-1.5 rounded-full overflow-hidden bg-base-content/10">
                      <div
                        className="bg-primary"
                        style={{ width: `${Math.round(details.venueScore.observedWeight * 100)}%` }}
                        title={`${Math.round(details.venueScore.observedWeight * 100)}% from guest ratings`}
                      />
                      <div
                        className="bg-base-content/25"
                        style={{ width: `${Math.round((1 - details.venueScore.observedWeight) * 100)}%` }}
                        title={`${Math.round((1 - details.venueScore.observedWeight) * 100)}% from the pre-experience prior`}
                      />
                    </div>
                    <div className="mt-1 text-[10px] text-dark-muted">
                      {details.venueScore.ratingCount === 0 ? (
                        <>Prior {details.venueScore.prior.toFixed(2)} only — no guest has been sent here yet</>
                      ) : (
                        <>
                          {Math.round(details.venueScore.observedWeight * 100)}% observed{" "}
                          {details.venueScore.observed !== null && (
                            <>({details.venueScore.observed.toFixed(2)} over {details.venueScore.ratingCount})</>
                          )}{" "}
                          · {Math.round((1 - details.venueScore.observedWeight) * 100)}% prior{" "}
                          ({details.venueScore.prior.toFixed(2)})
                        </>
                      )}
                      <span className="text-dark-muted/70"> · platform mean 3.60</span>
                    </div>

                    {details.venueScore.basis.length > 0 && (
                      <p className="mt-1 text-[10px] text-dark-muted/80 leading-relaxed">
                        {details.venueScore.basis.join("; ")}
                      </p>
                    )}

                    {!details.venueScore.isAudited && (
                      <div className="mt-1.5 flex items-start gap-1 text-[10px] text-warning">
                        <AlertTriangle className="w-3 h-3 shrink-0 mt-px" />
                        <span>
                          Not audited — prior is Google tags only. Noise, bill-splitting and
                          menu-breadth risks are unknown.
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Operating constraints — hard rules, not colour commentary */}
                {details.operatingNotes && details.operatingNotes.length > 0 && (
                  <div className="p-3 border-b border-dark-border">
                    <div className="flex items-center gap-1 mb-1.5">
                      <ShieldAlert className="w-3 h-3 text-warning" />
                      <span className="text-[10px] font-semibold text-warning uppercase tracking-wide">
                        Operating Constraints
                      </span>
                    </div>
                    <ul className="space-y-1">
                      {details.operatingNotes.map((note, i) => (
                        <li key={i} className="text-[11px] text-base-content/80 leading-relaxed">
                          {note}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Stats */}
                <div className="p-3 border-b border-dark-border">
                  <div className="flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-1">
                      <Utensils className="w-3 h-3 text-primary" />
                      <span className="font-medium">{details.totalDinners}</span>
                      <span className="text-dark-muted">dinner{details.totalDinners !== 1 ? "s" : ""}</span>
                    </div>
                    {stats && stats.responseCount > 0 && (
                      <>
                        <div className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-amber-400" />
                          <span className="font-medium">
                            {stats.avgExperience?.toFixed(1) || "—"}
                          </span>
                          <span className="text-dark-muted">/ 5</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-dark-muted">NPS</span>
                          <span className="font-medium">
                            {stats.avgNps?.toFixed(1) || "—"}
                          </span>
                        </div>
                        <div className="text-dark-muted">
                          {stats.responseCount} review{stats.responseCount !== 1 ? "s" : ""}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Rating distribution bar */}
                  {stats && totalRatings > 0 && (
                    <div className="mt-2">
                      <div className="flex h-2 rounded-full overflow-hidden bg-base-content/10">
                        {Object.entries(stats.ratingDistribution).map(([key, value]: [string, number]) => {
                          if (value === 0) return null;
                          const pct = (value / totalRatings) * 100;
                          const colors: Record<string, string> = {
                            perfect: "bg-success",
                            good: "bg-info",
                            okay: "bg-warning",
                            notGreat: "bg-orange-400",
                            poor: "bg-error",
                          };
                          return (
                            <div
                              key={key}
                              className={`${colors[key] || "bg-base-content/20"}`}
                              style={{ width: `${pct}%` }}
                              title={`${RATING_LABELS[key]?.label}: ${value}`}
                            />
                          );
                        })}
                      </div>
                      <div className="flex justify-between mt-1 text-[10px] text-dark-muted">
                        {Object.entries(stats.ratingDistribution).map(([key, value]: [string, number]) => {
                          if (value === 0) return null;
                          return (
                            <span key={key} className={RATING_LABELS[key]?.color}>
                              {RATING_LABELS[key]?.label} {value}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Past Visits from current table users */}
                {pastVisits && pastVisits.length > 0 && (
                  <div className="p-3 border-b border-dark-border">
                    <div className="flex items-center gap-1 mb-1.5">
                      <RotateCcw className="w-3 h-3 text-warning" />
                      <span className="text-[10px] font-semibold text-warning uppercase tracking-wide">
                        Repeat Visitors ({pastVisits.length})
                      </span>
                    </div>
                    <div className="space-y-1">
                      {pastVisits.map((visit, i) => (
                        <div key={`${visit.firstName}-${visit.date}-${i}`} className="flex items-center justify-between text-xs">
                          <span className="font-medium text-base-content/80">
                            {visit.firstName}
                          </span>
                          <span className="text-[10px] text-dark-muted">
                            {new Date(visit.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Feedback */}
                {details.recentFeedback.length > 0 && (
                  <div className="flex flex-col">
                    <div className="flex items-center gap-1 px-3 pt-3 pb-1">
                      <MessageSquare className="w-3 h-3 text-dark-muted" />
                      <span className="text-[10px] font-semibold text-dark-muted uppercase tracking-wide">
                        Feedback ({details.recentFeedback.length})
                      </span>
                    </div>
                    <div className="px-3 pb-3 max-h-64 overflow-y-auto space-y-2">
                      {details.recentFeedback.map((feedback: RestaurantHoverDetails['recentFeedback'][number], i: number) => (
                        <div key={i} className="text-xs">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            {feedback.firstName && (
                              <span className="font-medium text-base-content/80">
                                {feedback.firstName}
                              </span>
                            )}
                            {feedback.rating && (
                              <span className={`text-[10px] ${RATING_LABELS[feedback.rating]?.color || "text-dark-muted"}`}>
                                {RATING_LABELS[feedback.rating]?.label || feedback.rating}
                              </span>
                            )}
                            {feedback.date && (
                              <span className="text-[10px] text-dark-muted ml-auto">
                                {new Date(feedback.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                              </span>
                            )}
                          </div>
                          {feedback.note && (
                            <p className="text-dark-muted leading-relaxed">
                              "{feedback.note}"
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {(!stats || stats.responseCount === 0) && details.recentFeedback.length === 0 && (
                  <div className="p-3 text-center text-xs text-dark-muted">
                    No feedback yet
                  </div>
                )}
              </>
            )}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
